import NotAddedProductsPopup, { NotAddedProduct } from 'components/Pages/Customer/NotAddedProductsPopup';
import { AddToCartButtonPrimary } from 'components/Pages/ProductDetail/ProductDetailAddToCart/AddToCartButtonPrimary';
import { mapAddVirtualSetProductResultData } from 'connectors/cart/Cart';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { AddMultipleProductsByUuidInputApi } from 'graphql/generated';
import { isB2bDomain } from 'helpers/isDomainLocale';
import { useAddVirtualSetProductsToCart } from 'hooks/cart/useAddVirtualSetProductsToCart';
import useTranslation from 'next-translate/useTranslation';
import { useState } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { VirtualSetProductType } from 'types/product';

type AddToCartButtonProps = {
    includedProducts: VirtualSetProductType[];
    disabled?: boolean;
};

export const AddToCartButton: FC<AddToCartButtonProps> = ({ includedProducts, disabled }) => {
    const { t } = useTranslation();
    const domainConfig = useDomainConfig();
    const { addVirtualSetProductsToCart, addVirtualSetProductsMutationState } = useAddVirtualSetProductsToCart();
    const { updateUserState } = usePersistStore((s) => s);
    const [notAddedProducts, setNotAddedProducts] = useState<NotAddedProduct[]>();

    const onAddToCartClick = async () => {
        const productsInput: AddMultipleProductsByUuidInputApi[] = Array.from(includedProducts.values()).map(
            (product) => ({
                uuid: product.uuid,
                quantity: isB2bDomain(domainConfig) ? product.b2bMinimumProductOrder ?? 1 : 1,
            }),
        );

        const modifications = await addVirtualSetProductsToCart(productsInput);
        const notAddedProducts: NotAddedProduct[] = [
            ...(modifications?.itemsNotFound ?? []).map((name) => ({
                name,
                catnum: null,
            })),
            ...(modifications?.multipleAddedProductModifications.notAddedProducts ?? []).map((product) => ({
                name: product.fullName,
                catnum: product.catalogNumber,
            })),
        ];
        setNotAddedProducts(notAddedProducts);
        const addedProducts = modifications?.multipleAddedProductModifications.addedProducts ?? [];

        if (addedProducts.length > 0) {
            const mappedAddToCartResult = mapAddVirtualSetProductResultData(
                addedProducts[0],
                domainConfig.currencyCode,
            );

            if (mappedAddToCartResult) {
                const virtualSetTotalProductsQuantity = addedProducts.reduce(
                    (acc, product) => acc + product.addedQuantity,
                    0,
                );

                updateUserState({
                    addToCartPopupData: {
                        gtmListName: 'virtual set',
                        listIndex: 0,
                        addedAsVirtualSet: addedProducts.length > 1,
                        virtualSetTotalProductsQuantity: virtualSetTotalProductsQuantity,
                        virtualSetTotalProductsCount: addedProducts.length,
                        product: mappedAddToCartResult,
                    },
                });
            }
        }
    };

    return (
        <div>
            <AddToCartButtonPrimary
                buttonText={t('Add set to cart')}
                isBookingOnly={false}
                isDisabled={includedProducts.length === 0 || disabled}
                isLoading={addVirtualSetProductsMutationState.fetching}
                onClick={onAddToCartClick}
            />
            {!!notAddedProducts?.length && (
                <NotAddedProductsPopup
                    notAddedProducts={notAddedProducts}
                    onCloseCallback={() => setNotAddedProducts(undefined)}
                />
            )}
        </div>
    );
};
